import React from 'react';
import uri from "urijs";

import {config, N8N} from "../index";
import {n8n} from "../../lib/n8n";

import {LatchGroup} from '../components/latch-button';
import PictureButton from './picture-editor';
import TextButton from './text-editor';
import ColourButton from './colour-editor';
import SemanticButton from './semantic-colour-editor';
import CodeButton, {EverythingEditorButton} from './code-editor';

import '../../css/inject-iframe.css';
import {CommitButton, DownloadButton, OverwriteButton} from "./download";
import DeployEditor from "./deploy-editor";
import WireframeApi from "../../lib/wireframe";

export interface WireframeEditorProps {
	wireframe: Blob
}

export function selectElement(iframe: HTMLIFrameElement, abort: AbortSignal, mode: 'img' | 'default' = 'default'): Promise<HTMLElement> {
	return new Promise((ok, cancel) => {
		abort.addEventListener('abort', reason => {
			cancel(reason);
			throw reason;
		});

		const doc = iframe.contentDocument;

		if (!doc)
			throw new Error(`iframe does not have accessible content`);

		doc.documentElement.dataset.cursorSelectionMode = mode;
		doc.addEventListener('mouseup', e => {
			const el = doc?.elementFromPoint(e.clientX, e.clientY);

			if (!el) return;

			delete doc.documentElement.dataset.cursorSelectionMode;

			if (mode == 'default')
				return ok(el as HTMLElement);
			else if (mode == 'img')
				for (let parent: Element | null = el; parent; parent = parent?.parentElement ?? null)
					if (parent && ['img', 'picture', 'svg', 'image', 'video', 'canvas'].includes(parent?.tagName.toLowerCase()))
						return ok(parent as HTMLElement);
		});
	});
}

export const iframeCx = React.createContext<null | (() => HTMLIFrameElement & { contentDocument: Document, contentWindow: Window })>(null);

export default function WireframeEditor(props: WireframeEditorProps) {
	const ref = React.useRef<HTMLIFrameElement>(null);
	const html = React.useMemo(() => URL.createObjectURL(props.wireframe), [props.wireframe]);

	const n8nApi = React.useContext(N8N);
	const wireframeApi = React.useMemo(() => new WireframeApi(n8nApi), [n8nApi]);

	const init = React.useCallback(async () => {
		const doc = ref.current?.contentDocument;
		const win = ref.current?.contentWindow as Window & { CSSStyleSheet: typeof CSSStyleSheet };

		if (!doc || !win) return;

		const css = new win.CSSStyleSheet({})
		await css.replace(await fetch('/index.css', { // For some reason inject-iframe.css is coming out as index.css. Let's just not touch it...
			headers: {
				accept: 'text/css'
			}
		}).then(res => res.text()));

		doc.adoptedStyleSheets.push(css);
	}, [ref.current?.contentDocument, ref.current?.contentWindow]);

	return <section id='wireframe-editor'>
		<div id="editor-toolbar">

			<iframeCx.Provider value={() => {
				if (!ref.current || !ref.current.contentDocument || !ref.current.contentWindow)
					throw new Error(`iframe does not expose content`);

				return ref.current as HTMLIFrameElement & { contentDocument: Document, contentWindow: Window };
			}}>
				<LatchGroup.Provider value={Symbol('tool-buttons')}>
					<PictureButton />
					<TextButton />
					<SemanticButton />
					<CodeButton />
				</LatchGroup.Provider>
				<ColourButton />

				<hr />

				<EverythingEditorButton />

				<span style={{ flex: 1 }} />

				<OverwriteButton api={wireframeApi} />
				<CommitButton api={wireframeApi} />
				<DownloadButton api={wireframeApi} />

				<hr />

				<DeployEditor />
			</iframeCx.Provider>
		</div>

		{/*<IframeContent html={html} ref={ref} onLoad={init}/>*/}
		<iframe src={html} ref={ref} onLoad={init} id='editor-preview' />
	</section>;
}

export function useProjectNumber(): [string, string] {
	return ["", ""];
}